import * as store from '@attain-apps/fe/web/main/lib/store'

import * as ecModels from '../../../models'
import * as plugin from '../../plugin'

import { getActionsConfig } from './actions'
import { Config } from './config'
import * as effects from './effects'
import { Selectors } from './selectors'
import { State } from './state'

/**
 * There are many cases where we want to have one focused entity on the screen.
 * This could be a page dedicated to the entity, a modal where we are editing
 * the entity, an entity that makes up part of the current context (e.g. tenant
 * or app), etc. This plugin is used to manage the focused entity
 */
export class Plugin<
  TTypeParams extends ecModels.TypeParams.Constraint,
> extends plugin.Plugin<
  TTypeParams,
  plugin.Plugin.Id.Standard.CurrentEntity,
  State,
  Selectors<TTypeParams>,
  ReturnType<typeof getActionsConfig<TTypeParams>>
> {
  /**
   * Stores the current entity IDs
   */
  public readonly storage = new store.storage.included.SessionStorage({
    parent: this.feature,
    properties: {
      idLookup: store.storage.lookup<ecModels.Target, ecModels.EntityId>({
        defaultKey: ecModels.Target.DEFAULT,
      }),
    },
  })

  /**
   * @see {@link Config}
   */
  public readonly config: Readonly<Config<TTypeParams>>

  /**
   * In some cases, the current {@link ecModels.EntityId} for the
   * {@link ecModels.Target.DEFAULT} is desired in a _synchronous_ context (e.g.
   * an action transformer). In those cases, this value should represent what is currently
   * in the state.
   *
   * __NOTE:__ it is not recommended to use this unless you absolutely have to.
   * Using {@link Selectors.getId} is much more reliable
   */
  public get defaultCurrentId(): ecModels.EntityId | undefined {
    return this._defaultCurrentId
  }

  /** @see {@link _defaultCurrentId} */
  private _defaultCurrentId: ecModels.EntityId | undefined

  constructor({ collection, config }: Plugin.constructor.Params<TTypeParams>) {
    super({
      actions: getActionsConfig<TTypeParams>(),
      collection,
      id: plugin.Plugin.Id.Standard.CurrentEntity,
      initialState: State.INITIAL,
      selectors: () => new Selectors(this),
    })
    this.config = Config.from(config)

    this.feature.registerEffects(
      effects.executeGetEntity(this),
      effects.triggerGetEntity(this),
      effects.onChangeInCurrentList(this),
    )

    /** @see {@link defaultCurrentId} */
    // NOTE: the setTimeout is needed to allow this to run after all of the
    // requisitive items have finished being constructed
    setTimeout(() =>
      this.feature.selectors
        .getId()
        .asObservable.subscribe(id => (this._defaultCurrentId = id)),
    )
  }
}
export namespace Plugin {
  export namespace constructor {
    export type Params<TTypeParams extends ecModels.TypeParams.Constraint> =
      Pick<plugin.Plugin.Default<TTypeParams>, 'collection'> & {
        config?: Config.Options<TTypeParams>
      }
  }
}
